// import { Dialog, Grid, IconButton, Button, LinearProgress, CircularProgress, Tooltip, Table, TableContainer, Paper, TableHead, TableRow, TableCell, TableBody } from '@mui/material'
import React, { useEffect, useState, useRef } from "react";
import { getCommaSeperated, getuuid } from "../../../util/functions";

import {
  Dialog,
  Grid,
  IconButton,
  Button,
  LinearProgress,
  CircularProgress,
  Tooltip,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  Badge,
  TextField,
  InputAdornment,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BgComponent from "../../Auth/Components/BgComponent";
import Logo from "../../../assets/image/logo/logo.png";
import axios from "axios";
import { TAAPI_API_KEY } from "../../../shared/taapiConfig";
import { errorToaster, successToaster } from "../../../util/toaster";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  backtest,
  getIndicatorAPI,
  getLeftIndicatorAPI,
} from "../../../services/Stock";
import moment from "moment";
import { ALGO_STATUS } from "../../../shared/constant";
import { useUser } from "../../../context/UserContext";
import Supabase from "../../../backend/client";
import { useNavigate } from "react-router-dom";

const Backtest = ({
  open,
  handleClose,
  dndRequest,
  setAlgoResult,
  algoResult,
  backtestStats,
  setBacktestStats,
  loading,
  setLoading,
  setSelectedStockLst,
  selectedStockList,
}) => {
  const user = useUser();
  const userDetails = user?.user;
  const navigate = useNavigate();

  const [defaultStockList, setDefaultStockList] = useState([]);
  const [selectedStock, setSelectedStock] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredStockList, setFilteredStockList] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [selectedStockName, setSelectedStockName] = useState("");
  
  const searchInputRef = useRef(null);
  const suggestionListRef = useRef(null);

  const [initiateRun, setInitiateRun] = useState(false);

  useEffect(() => {
    const api = `https://api.taapi.io/exchange-symbols?secret=${TAAPI_API_KEY}&type=stocks`;
    axios
      .get(api)
      .then((res) => {
        const list = res?.data?.map((item) => ({ name: item, symbol: item }));
        setDefaultStockList(list);
        setFilteredStockList(list);
      })
      .catch((error) => {
        console.log("error", error);
      });
  }, []);

  // Filter stocks based on search term
  useEffect(() => {
    if (!defaultStockList || defaultStockList.length === 0) return;
    
    if (searchTerm.trim() === "") {
      setFilteredStockList(defaultStockList.slice(0, 100)); // Limit initial suggestions to prevent performance issues
      return;
    }
    
    const filtered = defaultStockList.filter(stock => 
      stock.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      stock.symbol.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 100); // Limit to 100 results for performance
    
    setFilteredStockList(filtered);
    setFocusedIndex(-1);
  }, [searchTerm, defaultStockList]);

  const handleSearchFocus = () => {
    setShowSuggestions(true);
  };

  const handleSearchBlur = (e) => {
    // Delay hiding suggestions to allow click to register
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    
    if (e.target.value === "") {
      // If search is cleared, also clear selection
      setSelectedStock("");
      setSelectedStockLst([]);
      setSelectedStockName("");
    }
    
    setShowSuggestions(true);
  };
  
  const handleSelectStock = (stock) => {
    setSelectedStock(stock.symbol);
    setSearchTerm(stock.symbol);
    setSelectedStockName(stock.name);
    setSelectedStockLst([stock]);
    setShowSuggestions(false);
  };
  
  const handleKeyDown = (e) => {
    // Handle keyboard navigation
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex(prev => 
        prev < filteredStockList.length - 1 ? prev + 1 : prev
      );
      
      // Auto-scroll to keep focused item in view
      if (suggestionListRef.current && focusedIndex >= 0) {
        const focusedElement = suggestionListRef.current.children[focusedIndex + 1];
        if (focusedElement) {
          focusedElement.scrollIntoView({ block: 'nearest' });
        }
      }
    } 
    else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex(prev => (prev > 0 ? prev - 1 : prev));
      
      // Auto-scroll to keep focused item in view
      if (suggestionListRef.current && focusedIndex > 0) {
        const focusedElement = suggestionListRef.current.children[focusedIndex - 1];
        if (focusedElement) {
          focusedElement.scrollIntoView({ block: 'nearest' });
        }
      }
    } 
    else if (e.key === "Enter" && focusedIndex >= 0) {
      e.preventDefault();
      const selectedStock = filteredStockList[focusedIndex];
      if (selectedStock) {
        handleSelectStock(selectedStock);
      }
    }
    else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };
  
  const clearSearch = () => {
    setSearchTerm("");
    setSelectedStock("");
    setSelectedStockLst([]);
    setSelectedStockName("");
    setFocusedIndex(-1);
    searchInputRef.current?.focus();
  };

  const isValidTimestamp = (timestamp) => {
    if (!timestamp) return false;

    const date = new Date(timestamp);
    return !isNaN(date.getTime());
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "No Date";

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "Invalid Date";

    return moment(date).format("DD MMMM, YYYY");
  };

  const runBacktest = async () => {
    if (!selectedStockList?.length)
      return errorToaster("Please select stocks.");

    setInitiateRun(true);
    setLoading(true);

    try {
      const backtestRes = await backtest(dndRequest, selectedStockList);

      console.log("[Backtest Results]", backtestRes);
      console.log("[Backtest Results Structure]", {
        hasResult: !!backtestRes,
        hasBacktestResult: !!backtestRes?.backtestResult,
        hasDetailedExecutionLog: !!backtestRes?.backtestResult?.detailedExecutionLog,
        hasBasicSummary: !!backtestRes?.backtestResult?.detailedExecutionLog?.basicSummary
      });

      if (backtestRes?.error) {
        errorToaster(`Backtest error: ${backtestRes.error}`);
        setLoading(false);
        return;
      }

      setBacktestStats(backtestRes?.backtestResult);
      console.log("[After setting backtestStats]", {
        currentBacktestStats: backtestRes?.backtestResult,
        hasDetailedExecutionLog: !!backtestRes?.backtestResult?.detailedExecutionLog,
        loading: false
      });
      
      setLoading(false); // Explicitly set loading to false
      
      const processedResults = Array.isArray(backtestRes?.result)
        ? backtestRes.result.map((item) => {
            if (item.error) {
              return {
                stock: { name: item.symbol, symbol: item.symbol },
                buyTxnObj: { success: false },
                sellTxnObj: { success: false },
                errorMessage: item.error,
              };
            }

            return item;
          })
        : [];

      setAlgoResult(processedResults);
    } catch (error) {
      console.error("Error running backtest:", error);
      errorToaster(`Failed to run backtest: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const tableHeader = { fontSize: 12, fontWeight: "bold" };
  const tableCell = { fontSize: 12 };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullScreen>
      <div className="flex flex-row w-full h-[100vh]">
        <div className="lg:flex flex-[0.40] xs:hidden">
          <BgComponent
            bgImg="bg-stock-bg"
            customBack={true}
            backAction={handleClose}>
            <p className="text-white xl:text-[50px] lg:text-[40px] font-bold absolute bottom-40 xl:left-28 lg:left-16">
              Freedom for <br />
              things that matter.
            </p>
          </BgComponent>
        </div>
        <div className="flex md:flex-[0.60] xs:flex-[0.80] flex-col lg:max-w-[550px] mx-auto xs:pt-[80px] md:pt-[40px] md:justify-center">
          <a href="/">
            <img src={Logo} className="relative xs:w-[120px] md:w-[170px]" />
          </a>
          {initiateRun ? (
            <div className="md:pt-6 pb-20">
              <h1 className="text-[40px] font-bold mb-6">Backtest Result</h1>
              
              <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Strategy Performance Summary</h2>
                
                {console.log("[UI Render Debug]", {
                  loading,
                  hasBacktestStats: !!backtestStats,
                  hasDetailedExecutionLog: !!backtestStats?.detailedExecutionLog,
                  hasBasicSummary: !!backtestStats?.detailedExecutionLog?.basicSummary
                })}

                {loading ? (
                  <div className="flex justify-center items-center py-10">
                    <CircularProgress />
                  </div>
                ) : backtestStats?.detailedExecutionLog ? (
                  <div>
                    {backtestStats.detailedExecutionLog.basicSummary ? (
                      <div className="mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                              <span className="text-blue-600 mr-1">📊</span>
                              Performance Metrics
                            </h3>
                            <div className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Total Return:</span>
                                <span className={`font-medium ${backtestStats.detailedExecutionLog.basicSummary?.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {backtestStats.detailedExecutionLog.basicSummary?.totalProfit > 0 ? '+' : ''}
                                  {parseFloat(backtestStats.detailedExecutionLog.basicSummary?.totalProfit).toFixed(2)}%
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Profit/Loss:</span>
                                <span className={`font-medium ${backtestStats.detailedExecutionLog.basicSummary?.totalReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {backtestStats.detailedExecutionLog.basicSummary?.totalReturn >= 0 ? '+$' : '-$'}
                                  {Math.abs(backtestStats.detailedExecutionLog.basicSummary?.totalReturn).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Annualized Return:</span>
                                <span className={`font-medium ${backtestStats.detailedExecutionLog.basicSummary?.annualizedReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {backtestStats.detailedExecutionLog.basicSummary?.annualizedReturn > 0 ? '+' : ''}
                                  {parseFloat(backtestStats.detailedExecutionLog.basicSummary?.annualizedReturn).toFixed(2)}%
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Max Drawdown:</span>
                                <span className="font-medium text-red-600">
                                  {backtestStats.detailedExecutionLog.basicSummary?.maxDrawdownPercent > 0 ? '-' : ''}
                                  {parseFloat(backtestStats.detailedExecutionLog.basicSummary?.maxDrawdownPercent).toFixed(2)}%
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                              <span className="text-blue-600 mr-1">📈</span>
                              Trade Statistics
                            </h3>
                            <div className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Number of Trades:</span>
                                <span className="font-medium">{backtestStats.detailedExecutionLog.basicSummary?.tradeCount}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Win Rate:</span>
                                <div className="flex items-center">
                                  <span className="font-medium text-green-600">
                                    {backtestStats.detailedExecutionLog.basicSummary?.winRate}%
                                  </span>
                                  <div className="w-16 h-2 bg-gray-200 ml-2 rounded-full overflow-hidden">
                                    <div 
                                      className="h-2 bg-green-500 rounded-full" 
                                      style={{width: `${Math.min(100, backtestStats.detailedExecutionLog.basicSummary?.winRate)}%`}}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Best Trade / Worst Trade:</span>
                                <div>
                                  <span className="font-medium text-green-600">
                                    {backtestStats.detailedExecutionLog.basicSummary?.biggestWinPercent > 0 ? 
                                      '+' + backtestStats.detailedExecutionLog.basicSummary?.biggestWinPercent + '%' : 
                                      '0.00%'}
                                  </span>
                                  <span className="mx-1">/</span>
                                  <span className="font-medium text-red-600">
                                    {backtestStats.detailedExecutionLog.basicSummary?.biggestLossPercent < 0 ? 
                                      backtestStats.detailedExecutionLog.basicSummary?.biggestLossPercent + '%' : 
                                      '0.00%'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <p className="text-gray-500">Performance metrics data could not be loaded. Please try again.</p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                          <span className="text-blue-600 mr-1">⚙️</span>
                          Strategy Parameters
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <span className="text-xs text-gray-500 w-24">Symbol:</span>
                              <span className="text-sm font-medium">{backtestStats.detailedExecutionLog.initialization?.symbol || 'N/A'}</span>
                            </div>
                            <div className="flex items-center">
                              <span className="text-xs text-gray-500 w-24">Initial Capital:</span>
                              <span className="text-sm font-medium">${backtestStats.detailedExecutionLog.initialization?.initialCapital?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || 'N/A'}</span>
                            </div>
                            <div className="flex items-center">
                              <span className="text-xs text-gray-500 w-24">Position Size:</span>
                              <span className="text-sm font-medium">{(backtestStats.detailedExecutionLog.initialization?.positionSize * 100)?.toFixed(0) || 'N/A'}%</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <span className="text-xs text-gray-500 w-24">RSI Period:</span>
                              <span className="text-sm font-medium">{backtestStats.detailedExecutionLog.initialization?.rsiPeriod || 'N/A'}</span>
                            </div>
                            <div className="flex items-center">
                              <span className="text-xs text-gray-500 w-24">Buy Threshold:</span>
                              <span className="text-sm font-medium">RSI &lt; {backtestStats.detailedExecutionLog.initialization?.rsiLowerThreshold || 'N/A'}</span>
                            </div>
                            <div className="flex items-center">
                              <span className="text-xs text-gray-500 w-24">Sell Threshold:</span>
                              <span className="text-sm font-medium">RSI &gt; {backtestStats.detailedExecutionLog.initialization?.rsiUpperThreshold || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                          <span className="text-blue-600 mr-1">📅</span>
                          Data Information
                        </h3>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <span className="text-xs text-gray-500 w-24">Period:</span>
                            <span className="text-sm font-medium">
                              {backtestStats.detailedExecutionLog.dataProcessing?.dateRange?.start || 'N/A'} to {backtestStats.detailedExecutionLog.dataProcessing?.dateRange?.end || 'N/A'}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <span className="text-xs text-gray-500 w-24">Data Points:</span>
                            <span className="text-sm font-medium">{backtestStats.detailedExecutionLog.dataProcessing?.daysProcessed || '0'} days</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                      <h2 className="text-xl font-semibold text-gray-800 mb-4">Trading Signals</h2>
                      
                      {backtestStats?.detailedExecutionLog?.signals && backtestStats.detailedExecutionLog.signals.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white">
                            <thead>
                              <tr>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Buy Signal</th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Buy Price</th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Buy RSI</th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Sell Signal</th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Sell Price</th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Sell RSI</th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Result</th>
                              </tr>
                            </thead>
                            <tbody>
                              {backtestStats.detailedExecutionLog.signals.map((signal, index) => (
                                <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                  <td className="py-2 px-4 border-b border-gray-200">{signal.buy?.date || 'N/A'}</td>
                                  <td className="py-2 px-4 border-b border-gray-200">${signal.buy?.price?.toFixed(2) || 'N/A'}</td>
                                  <td className="py-2 px-4 border-b border-gray-200">{signal.buy?.rsi?.toFixed(2) || 'N/A'}</td>
                                  <td className="py-2 px-4 border-b border-gray-200">{signal.sell?.date || 'Open'}</td>
                                  <td className="py-2 px-4 border-b border-gray-200">{signal.sell ? `$${signal.sell.price.toFixed(2)}` : 'Open'}</td>
                                  <td className="py-2 px-4 border-b border-gray-200">{signal.sell ? signal.sell.rsi.toFixed(2) : 'Open'}</td>
                                  <td className={`py-2 px-4 border-b border-gray-200 ${signal.result?.profit > 0 ? 'text-green-600' : signal.result?.profit < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                    {signal.result ? `${signal.result.percentProfit > 0 ? '+' : ''}${signal.result.percentProfit.toFixed(2)}%` : 'Open'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 py-4">
                          No trading signals were generated during the backtest period.
                        </div>
                      )}
                    </div>
                    
                    {/* Add open position summary section */}
                    {backtestStats?.open_position && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-6">
                        <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                          <span className="text-blue-600 mr-1">🔄</span>
                          Open Position at End of Backtest
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <span className="text-xs text-gray-500 w-32">Symbol:</span>
                              <span className="text-sm font-medium">{backtestStats.detailedExecutionLog.initialization?.symbol || 'N/A'}</span>
                            </div>
                            <div className="flex items-center">
                              <span className="text-xs text-gray-500 w-32">Shares Held:</span>
                              <span className="text-sm font-medium">{backtestStats.open_position.shares.toLocaleString()}</span>
                            </div>
                            <div className="flex items-center">
                              <span className="text-xs text-gray-500 w-32">Entry Price:</span>
                              <span className="text-sm font-medium">${backtestStats.open_position.entry_price.toFixed(2)}</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <span className="text-xs text-gray-500 w-32">Current Price:</span>
                              <span className="text-sm font-medium">${backtestStats.open_position.current_price.toFixed(2)}</span>
                            </div>
                            <div className="flex items-center">
                              <span className="text-xs text-gray-500 w-32">Position Value:</span>
                              <span className="text-sm font-medium">${backtestStats.open_position.value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                            <div className="flex items-center">
                              <span className="text-xs text-gray-500 w-32">Unrealized P/L:</span>
                              <span className={`text-sm font-medium ${backtestStats.open_position.unrealized_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {backtestStats.open_position.unrealized_profit >= 0 ? '+' : ''}
                                ${backtestStats.open_position.unrealized_profit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                ({((backtestStats.open_position.current_price / backtestStats.open_position.entry_price - 1) * 100).toFixed(2)}%)
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs text-gray-500 italic">
                            Note: This position remains open at the end of the backtest period. In a real trading scenario, you would continue to hold this position.
                          </p>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex justify-between mt-6">
                      <Button 
                        variant="contained" 
                        color="primary"
                        onClick={() => handleClose()}
                        startIcon={<ArrowBackIcon />}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Back to Algo Engine
                      </Button>
                      
                      <div>
                        <Button 
                          variant="outlined" 
                          color="primary" 
                          onClick={() => {
                            // Instead of just clearing backtestStats
                            // We need to properly preserve the algo state
                            setBacktestStats(null);
                            // Reset only the part we need for a new test, not the entire state
                            setLoading(false);
                          }}
                          className="mx-1"
                        >
                          Run New Backtest
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <p className="text-gray-500 mb-2">No backtest results available.</p>
                    <p className="text-sm text-gray-400">Please check your selected stock and strategy parameters, then try again.</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="xs:py-4 md:py-12 xl:px-0">
              <p className="xs:text-[22px] md:text-[35px] font-bold font-sans mb-6">
                Select a stock to backtest
              </p>
              
              <div className="mb-6 max-w-md">
                <div className="relative">
                  <TextField
                    inputRef={searchInputRef}
                    fullWidth
                    variant="outlined"
                    label="Search for a stock"
                    placeholder="Enter stock symbol or name (e.g. AAPL, Tesla)"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onFocus={handleSearchFocus}
                    onBlur={handleSearchBlur}
                    onKeyDown={handleKeyDown}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton 
                            edge="end" 
                            onClick={clearSearch}
                            size="small"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  
                  {/* Stock suggestion dropdown */}
                  {showSuggestions && filteredStockList.length > 0 && (
                    <div 
                      ref={suggestionListRef}
                      className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm"
                      style={{ overflowY: 'auto' }}
                    >
                      {filteredStockList.map((stock, index) => (
                        <div
                          key={index}
                          className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50 ${
                            focusedIndex === index ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => handleSelectStock(stock)}
                        >
                          <div className="flex items-center">
                            <div className="font-medium">{stock.symbol}</div>
                            <div className="ml-2 text-gray-500">{stock.name !== stock.symbol ? ` - ${stock.name}` : ''}</div>
                          </div>
                          
                          {selectedStock === stock.symbol && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                              <CheckIcon color="primary" fontSize="small" />
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {showSuggestions && searchTerm && filteredStockList.length === 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-4 px-3 text-center text-gray-500">
                      No stocks found matching "{searchTerm}"
                    </div>
                  )}
                </div>
                
                {selectedStock && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-md border border-blue-100 flex items-center justify-between">
                    <div>
                      <div className="font-bold">{selectedStock}</div>
                      {selectedStockName !== selectedStock && (
                        <div className="text-sm text-gray-600">{selectedStockName}</div>
                      )}
                    </div>
                    <IconButton 
                      edge="end" 
                      onClick={clearSearch}
                      size="small"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <Button 
                  variant="contained" 
                  size="large"
                  onClick={runBacktest}
                  disabled={!selectedStockList.length}
                  className="py-3 px-6"
                >
                  Run Backtest
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default Backtest;
